/**
 * Lipstick Try-On — vanilla JS port of the Next.js LipstickTryOn flow.
 *
 * Public DOM contract:
 *   [data-tryon-open]              click → opens the modal
 *   [data-tryon-modal]             the modal root (hidden by default)
 *   [data-tryon-close]             closes the modal
 *   [data-tryon-models]            container for 6 model thumbnails (rendered by JS)
 *   [data-tryon-preview-stage]     holds the model image + lip overlay (and compare slider when active)
 *   [data-tryon-model-image]       <img> showing the currently-selected model face
 *   [data-tryon-lips]              wrapper that receives the inline SVG mask + multiply blend
 *   [data-tryon-compare-toggle]    toggles the before/after compare slider
 *   [data-tryon-variant-strip]     the shade swatches (rendered by tryon-popup-variants.liquid)
 *   [data-tryon-atc]               the single "Add to cart" CTA in the modal
 *   [data-tryon-toast]             slide-in success/error toast
 *   [data-tryon-coachmark]         first-time Compare coachmark
 *
 * State persistence (mirrors the Next.js implementation):
 *   localStorage.lipstickColor   - hex of the most-recently selected shade
 *   localStorage.tryonShowCompare - "true" once the user has seen the Compare coachmark
 *   sessionStorage.tryon_lipdata_v4 - cached modelLipsData2.json (Shopify Files CDN)
 */
(function () {
  'use strict';

  // -------------------------------------------------------------------------
  // Constants
  // -------------------------------------------------------------------------
  var MODELS = [
    'https://cdn.shopify.com/s/files/1/0721/7558/3279/files/model-1.jpg',
    'https://cdn.shopify.com/s/files/1/0721/7558/3279/files/model-2.jpg',
    'https://cdn.shopify.com/s/files/1/0721/7558/3279/files/model-3.jpg',
    'https://cdn.shopify.com/s/files/1/0721/7558/3279/files/model-4.jpg',
    'https://cdn.shopify.com/s/files/1/0721/7558/3279/files/model-5.jpg',
    'https://cdn.shopify.com/s/files/1/0721/7558/3279/files/model-6.jpg'
  ];

  var LIP_JSON_URL = 'https://cdn.shopify.com/s/files/1/0721/7558/3279/files/modelLipsData2.json';
  var LIP_CACHE_KEY = 'tryon_lipdata_v4';
  var COMPARE_SEEN_KEY = 'tryonShowCompare';

  // -------------------------------------------------------------------------
  // Module state
  // -------------------------------------------------------------------------
  var state = {
    open: false,
    modelUrl: MODELS[0],
    color: null,
    modelLips: null,
    compareMode: false,
    compareSlider: null,
    scrollY: 0
  };

  // Latest cart snapshot for instant cart-bar updates (no network wait).
  var lastKnownCart = null;

  // -------------------------------------------------------------------------
  // INP-friendly task chunker (matches the Next.js handleFunctionExecution)
  // -------------------------------------------------------------------------
  function yieldToMain() {
    return new Promise(function (resolve) { setTimeout(resolve, 0); });
  }

  async function runTasks(tasks) {
    for (var i = 0; i < tasks.length; i++) {
      try { tasks[i](); } catch (e) { /* swallow */ }
      await yieldToMain();
    }
  }

  // -------------------------------------------------------------------------
  // Analytics
  // -------------------------------------------------------------------------
  function track(event, payload) {
    payload = payload || {};
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: event }, payload));
    } catch (e) {}
    try {
      if (window.Moengage && typeof window.Moengage.track_event === 'function') {
        window.Moengage.track_event(event, payload);
      }
    } catch (e) {}
  }

  // -------------------------------------------------------------------------
  // Lip mask data (cached per session)
  // -------------------------------------------------------------------------
  async function loadLipData() {
    if (state.modelLips) return state.modelLips;
    try {
      var cached = sessionStorage.getItem(LIP_CACHE_KEY);
      if (cached) {
        state.modelLips = JSON.parse(cached);
        return state.modelLips;
      }
    } catch (e) {}

    try {
      var res = await fetch(LIP_JSON_URL);
      var json = await res.json();
      state.modelLips = json && json.modelLipsData;
      try { sessionStorage.setItem(LIP_CACHE_KEY, JSON.stringify(state.modelLips)); } catch (e) {}
    } catch (e) {
      state.modelLips = null;
    }
    return state.modelLips;
  }

  function findLipsIndex(url) {
    if (!url) return -1;
    for (var i = 6; i >= 1; i--) {
      if (url.indexOf('model-' + i) !== -1) return i - 1;
    }
    return -1;
  }

  // Verbatim port of modules/Product/components/TryOn/assets/index.js ModalLips.
  // Four nested <g> wrappers + viewBox 0 0 200 200 + no explicit preserveAspectRatio
  // (defaults to xMidYMid meet) are exactly what the modelLipsData2.json path
  // coordinates were calibrated against — do not change without recalibrating.
  //
  // Important: some entries in modelLipsData2.json (models 2, 3, 6) DO NOT
  // include `imageWidth` / `imageHeight`. In Next.js / React, an `undefined`
  // attribute is omitted from the rendered HTML, so the <image> falls back to
  // the natural pixel size of the embedded base64 data URL. We have to
  // replicate that here — if we emit `width="undefined"` the mask alpha
  // channel breaks and the lipstick won't paint on the lips.
  function attr(name, value) {
    return (value == null || value === '') ? '' : ' ' + name + '="' + value + '"';
  }

  function buildLipsSvg(d, color) {
    var imageAttrs = ''
      + attr('width', d.imageWidth)
      + attr('height', d.imageHeight)
      + attr('transform', d.transform)
      + attr('xlink:href', d.xlinkHref)
      + attr('href', d.xlinkHref);

    return ''
      + '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 200 200">'
      +   '<defs>'
      +     '<filter id="luminosity-noclip" x="' + d.x + '" y="' + d.y + '" width="' + d.width + '" height="' + d.height + '" color-interpolation-filters="sRGB" filterUnits="userSpaceOnUse">'
      +       '<feFlood flood-color="#fff" result="bg"/>'
      +       '<feBlend in="SourceGraphic" in2="bg"/>'
      +     '</filter>'
      +     '<mask id="mask" x="' + d.x + '" y="' + d.y + '" width="' + d.width + '" height="' + d.height + '" maskUnits="userSpaceOnUse">'
      +       '<g style="filter:url(#luminosity-noclip)">'
      +         '<image' + imageAttrs + '/>'
      +       '</g>'
      +     '</mask>'
      +   '</defs>'
      +   '<g style="isolation:isolate;mix-blend-mode:multiply">'
      +     '<g id="Layer_1" data-name="Layer 1">'
      +       '<g style="mix-blend-mode:multiply">'
      +         '<g style="mask:url(#mask)">'
      +           '<path d="' + d.d + '" style="fill:' + color + ';mix-blend-mode:multiply"/>'
      +         '</g>'
      +       '</g>'
      +     '</g>'
      +   '</g>'
      + '</svg>';
  }

  function renderLips(modal) {
    var wrap = modal.querySelector('[data-tryon-lips]');
    if (!wrap) return;
    var idx = findLipsIndex(state.modelUrl);
    if (idx < 0 || !state.modelLips || !state.color) {
      wrap.innerHTML = '';
      return;
    }
    var d = state.modelLips[idx];
    if (!d) { wrap.innerHTML = ''; return; }
    wrap.innerHTML = buildLipsSvg(d, state.color);
  }

  // -------------------------------------------------------------------------
  // Model thumbnail strip + preview image
  // -------------------------------------------------------------------------
  function renderPreview(modal) {
    var img = modal.querySelector('[data-tryon-model-image]');
    if (img) img.src = state.modelUrl;
    renderLips(modal);
  }

  function renderModelStrip(modal) {
    var strip = modal.querySelector('[data-tryon-models]');
    if (!strip) return;
    var html = '';
    for (var i = 0; i < MODELS.length; i++) {
      var url = MODELS[i];
      var selected = state.modelUrl === url;
      html += ''
        + '<button type="button" class="tryon-popup__model-btn' + (selected ? ' is-selected' : '') + '" data-tryon-model="' + url + '" aria-pressed="' + (selected ? 'true' : 'false') + '">'
        +   '<img src="' + url + '" alt="" width="56" height="56" loading="lazy">'
        + '</button>';
    }
    strip.innerHTML = html;
  }

  function onClickModelThumb(modal, url) {
    if (state.modelUrl === url) return;
    state.modelUrl = url;
    runTasks([
      function () { renderModelStrip(modal); },
      // renderPreview() only updates the regular preview stage. In compare
      // mode that DOM has been replaced, so we refresh via the slider
      // instead — it re-reads getModelUrl() (bottom image) AND
      // getLipsHtml() (top SVG uses the new model's lip data).
      function () {
        if (state.compareMode && state.compareSlider) {
          state.compareSlider.refresh();
        } else {
          renderPreview(modal);
        }
      },
      function () { track('change_model', { model: url }); }
    ]);
  }

  // -------------------------------------------------------------------------
  // Variant strip → product card sync (image / title / rating / price / ATC)
  // -------------------------------------------------------------------------
  function setText(modal, sel, value) {
    var el = modal.querySelector(sel);
    if (el) el.textContent = value;
  }

  function setHidden(modal, sel, hide) {
    var el = modal.querySelector(sel);
    if (!el) return;
    if (hide) el.setAttribute('hidden', '');
    else el.removeAttribute('hidden');
  }

  function syncTryonCardFromVariant(modal) {
    var atcBtn = modal.querySelector('[data-tryon-atc]');
    var strip = modal.querySelector('[data-tryon-variant-strip]');
    if (!atcBtn || !strip) return;
    var stripRoot = strip.querySelector('.tryon-variant-picker');
    if (!stripRoot) return;

    var selectedBtn = stripRoot.querySelector('button.tryon-v-btn.is-selected')
                   || stripRoot.querySelector('button.tryon-v-btn');
    if (!selectedBtn) return;

    var vid = selectedBtn.getAttribute('data-variant-id');
    var price = selectedBtn.getAttribute('data-variant-price') || '';
    var mrp = selectedBtn.getAttribute('data-variant-mrp') || '';
    var discount = selectedBtn.getAttribute('data-variant-discount') || '';
    var available = selectedBtn.getAttribute('data-variant-available') === 'true';
    var image = selectedBtn.getAttribute('data-image') || '';
    var url = selectedBtn.getAttribute('data-url') || '';
    var productTitle = selectedBtn.getAttribute('data-product-title') || '';
    var rating = selectedBtn.getAttribute('data-product-rating') || '';
    var reviewCount = parseInt(selectedBtn.getAttribute('data-product-review-count') || '0', 10);

    // ATC button state
    if (vid) atcBtn.setAttribute('data-selected-variant-id', vid);
    if (available) {
      atcBtn.disabled = false;
      atcBtn.textContent = 'Add to Cart';
    } else {
      atcBtn.disabled = true;
      atcBtn.textContent = 'Sold out';
    }

    // Card image + link
    var imgEl = modal.querySelector('[data-tryon-card-image]');
    if (imgEl) {
      if (image) imgEl.src = image;
      imgEl.alt = productTitle;
    }
    var links = modal.querySelectorAll('[data-tryon-card-link]');
    for (var i = 0; i < links.length; i++) {
      if (url) links[i].setAttribute('href', url);
    }

    // Card title
    setText(modal, '[data-tryon-card-title]', productTitle);

    // Rating + review count
    if (rating) {
      setText(modal, '[data-tryon-card-rating]', rating);
      setHidden(modal, '[data-tryon-card-rating-wrap]', false);
    } else {
      setHidden(modal, '[data-tryon-card-rating-wrap]', true);
    }
    if (reviewCount > 0) {
      setText(modal, '[data-tryon-card-reviews]', reviewCount + ' Reviews');
      setHidden(modal, '[data-tryon-card-reviews-wrap]', false);
    } else {
      setHidden(modal, '[data-tryon-card-reviews-wrap]', true);
    }

    // Price / MRP / discount
    setText(modal, '[data-tryon-card-price]', price);
    if (mrp) {
      setText(modal, '[data-tryon-card-mrp]', mrp);
      setHidden(modal, '[data-tryon-card-mrp-wrap]', false);
    } else {
      setHidden(modal, '[data-tryon-card-mrp-wrap]', true);
    }
    if (discount) {
      setText(modal, '[data-tryon-card-discount]', discount);
      setHidden(modal, '[data-tryon-card-discount]', false);
    } else {
      setHidden(modal, '[data-tryon-card-discount]', true);
    }
  }

  // Back-compat alias — earlier code paths referenced this name.
  var syncTryonAtcFromVariant = syncTryonCardFromVariant;

  // Update underlying PDP <form action="/cart/add"> so the sticky ATC stays in sync.
  // Verbatim from creator-reels.liquid's syncPageProductFormVariantId.
  function syncPageProductFormVariantId(variantId) {
    if (variantId == null || variantId === '') return;
    var idStr = String(variantId);
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      var idInput = forms[i].querySelector('input[name="id"]');
      if (!idInput) continue;
      idInput.removeAttribute('disabled');
      idInput.value = idStr;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
      idInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
    document.dispatchEvent(new CustomEvent('tryon:variant-selected', {
      bubbles: true,
      detail: { variantId: parseInt(idStr, 10) }
    }));
  }

  function onClickShadeSwatch(modal, btn) {
    if (btn.classList.contains('tryon-v-btn--oos')) return;

    var strip = modal.querySelector('[data-tryon-variant-strip]');
    strip.querySelectorAll('button.tryon-v-btn').forEach(function (el) {
      el.classList.remove('is-selected');
    });
    btn.classList.add('is-selected');

    var hex = btn.getAttribute('data-hex');
    var handle = btn.getAttribute('data-handle');
    var vid = btn.getAttribute('data-variant-id');

    if (hex) {
      state.color = hex;
      try { localStorage.setItem('lipstickColor', hex); } catch (e) {}
    }

    runTasks([
      function () { syncTryonCardFromVariant(modal); },
      function () { syncPageProductFormVariantId(vid); },
      // In compare mode the regular [data-tryon-lips] element has been
      // replaced by the compare-slider markup, so renderLips() is a no-op.
      // We need to imperatively refresh the slider's top SVG instead — this
      // mirrors the way the Next.js <ComparisonSlider> re-renders when its
      // `choosedColor` prop changes.
      function () { refreshLipsAnywhere(modal); },
      function () { track('shades_change', { sku: handle, color: hex }); }
    ]);
  }

  // Repaint the lip overlay wherever it currently lives — either the
  // regular preview stage or the compare slider's top layer.
  function refreshLipsAnywhere(modal) {
    if (state.compareMode && state.compareSlider) {
      state.compareSlider.refresh();
    } else {
      renderLips(modal);
    }
  }

  // -------------------------------------------------------------------------
  // Compare slider
  // -------------------------------------------------------------------------
  function ensureCompareSlider() {
    if (state.compareSlider) return state.compareSlider;
    if (window.TryOnCompareSlider) {
      state.compareSlider = new window.TryOnCompareSlider();
    }
    return state.compareSlider;
  }

  function onClickCompareToggle(modal, btn) {
    state.compareMode = !state.compareMode;
    btn.setAttribute('aria-pressed', state.compareMode ? 'true' : 'false');
    var label = btn.querySelector('.tryon-popup__compare-label');
    if (label) label.textContent = state.compareMode ? 'Exit compare' : 'Compare';

    var stage = modal.querySelector('[data-tryon-preview-stage]');
    if (!stage) return;

    if (state.compareMode) {
      track('compare_click', {});
      var slider = ensureCompareSlider();
      if (slider) slider.mount(stage, {
        getModelUrl: function () { return state.modelUrl; },
        getLipsHtml: function () {
          var idx = findLipsIndex(state.modelUrl);
          if (idx < 0 || !state.modelLips || !state.color) return '';
          return buildLipsSvg(state.modelLips[idx], state.color);
        },
        onSliderMove: function () { track('move_slider_tryon', {}); }
      });
      maybeShowCompareCoachmark(modal);
    } else {
      var slider = state.compareSlider;
      if (slider) slider.unmount();
      renderPreview(modal);
    }
  }

  function maybeShowCompareCoachmark(modal) {
    try {
      if (localStorage.getItem(COMPARE_SEEN_KEY) === 'true') return;
    } catch (e) {}
    var coachmark = modal.querySelector('[data-tryon-coachmark]');
    if (!coachmark) return;
    setTimeout(function () {
      coachmark.hidden = false;
      coachmark.classList.add('is-visible');
    }, 1500);
  }

  function dismissCompareCoachmark(modal) {
    var coachmark = modal.querySelector('[data-tryon-coachmark]');
    if (coachmark) {
      coachmark.hidden = true;
      coachmark.classList.remove('is-visible');
    }
    try { localStorage.setItem(COMPARE_SEEN_KEY, 'true'); } catch (e) {}
    track('compare_popup_close', {});
  }

  // -------------------------------------------------------------------------
  // Toast (after ATC)
  // -------------------------------------------------------------------------
  var toastTimer = null;
  function showToast(modal, message, isError) {
    var toast = modal.querySelector('[data-tryon-toast]');
    if (!toast) return;
    var text = toast.querySelector('[data-tryon-toast-text]');
    if (text) text.textContent = message;
    toast.classList.toggle('is-error', !!isError);
    if (toastTimer) clearTimeout(toastTimer);
    requestAnimationFrame(function () { toast.classList.add('is-visible'); });
    toastTimer = setTimeout(function () { toast.classList.remove('is-visible'); }, 2500);
  }

  // -------------------------------------------------------------------------
  // Add to cart
  // -------------------------------------------------------------------------
  function normalizeAddLineResponse(data) {
    if (!data || typeof data !== 'object') return null;
    if (Array.isArray(data.items) && data.items.length) return data.items[0];
    if (data.id != null) return data;
    return null;
  }

  function dispatchCartRefreshWithCart(cart) {
    document.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: cart ? { cart: cart } : undefined
    }));
  }

  async function fetchCartAndRefresh(modal) {
    try {
      var cartRes = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!cartRes.ok) {
        dispatchCartRefreshWithCart(null);
        return;
      }
      var cart = await cartRes.json();
      lastKnownCart = cart;
      applyCartBarState(modal, cart, true);
      dispatchCartRefreshWithCart(cart);
    } catch (e) {
      dispatchCartRefreshWithCart(null);
    }
  }

  async function onClickAtc(modal, btn) {
    if (btn.disabled) return;
    var vid = (btn.getAttribute('data-selected-variant-id')
            || btn.getAttribute('data-default-variant-id') || '').trim();
    var idNum = parseInt(vid, 10);
    if (isNaN(idNum)) return;

    btn.disabled = true;
    var prevLabel = btn.textContent;
    btn.textContent = 'Adding…';

    try {
      var res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: idNum, quantity: 1 }] })
      });
      var data = await res.json().catch(function () { return {}; });
      if (res.ok) {
        showToast(modal, 'Product has been added to cart');
        track('try_on_atc', { variant_id: idNum });
        applyOptimisticCartAfterAdd(modal, normalizeAddLineResponse(data));
        await fetchCartAndRefresh(modal);
      } else {
        showToast(modal, (data && (data.description || data.message)) || 'Could not add to cart', true);
      }
    } catch (e) {
      showToast(modal, 'Could not add to cart', true);
    } finally {
      btn.disabled = false;
      btn.textContent = prevLabel;
      syncTryonCardFromVariant(modal);
    }
  }

  // -------------------------------------------------------------------------
  // Sticky bottom bar — visible only when the cart has items.
  // -------------------------------------------------------------------------
  function formatCartMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      var format = (window.theme && window.theme.moneyFormat) || Shopify.money_format || '{{amount}}';
      return Shopify.formatMoney(cents, format);
    }
    return '₹' + (cents / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  function roundCartToPayCents(cents) {
    return Math.round((cents || 0) / 100) * 100;
  }

  function discountTotalToCents(raw) {
    var rawNum = Number(raw);
    if (!isFinite(rawNum)) return null;
    var isWhole = Math.abs(rawNum - Math.round(rawNum)) < 1e-9;
    return isWhole && rawNum >= 10000 ? Math.round(rawNum) : Math.round(rawNum * 100);
  }

  // Mirrors cart-drawer / copy-code.js to-pay: net subtotal + shipping − prepaid discount.
  function getTryonShippingFeeCents() {
    return (window.__copyCodeCouponUi && window.__copyCodeCouponUi.shippingFeeCents) || 4000;
  }

  function getTryonFreeShippingThresholdCents() {
    return (window.__copyCodeCouponUi && window.__copyCodeCouponUi.freeShippingThresholdCents) || 39900;
  }

  function getTryonNetSubtotalCents(cart) {
    if (!cart) return 0;
    if (cart._displaySource === 'updatedCart' && cart.discount && cart.discount.total_price != null) {
      var fromDiscount = discountTotalToCents(cart.discount.total_price);
      if (fromDiscount != null) return fromDiscount;
    }
    return Number(cart.total_price) || 0;
  }

  function computeTryonCartToPayCents(cart) {
    var displayCart =
      typeof window.getDisplayCart === 'function' ? window.getDisplayCart(cart) : cart;
    if (!displayCart) return 0;

    if (window.updatedCartToPayAmount != null && window.updatedCartToPayAmount !== '') {
      var overrideCents = Math.round(Number(window.updatedCartToPayAmount) * 100);
      if (isFinite(overrideCents)) return overrideCents;
    }

    var netSubtotalCents = getTryonNetSubtotalCents(displayCart);
    var shippingCents =
      netSubtotalCents > getTryonFreeShippingThresholdCents() ? 0 : getTryonShippingFeeCents();
    var prepaidPercent =
      (window.__copyCodeCouponUi && window.__copyCodeCouponUi.prepaidDiscountPercent) || 5;
    var prepaidMaxCents =
      (window.__copyCodeCouponUi && window.__copyCodeCouponUi.prepaidDiscountMaxCents) || 5000;
    var prepaidDiscountCents = Math.min(
      Math.round(netSubtotalCents * prepaidPercent / 100),
      prepaidMaxCents
    );
    return netSubtotalCents + shippingCents - prepaidDiscountCents;
  }

  function getCartToPayDisplayText(cart, preferCartData) {
    if (preferCartData) {
      return formatCartMoney(roundCartToPayCents(computeTryonCartToPayCents(cart)));
    }

    var checkoutPayEl = document.querySelector('.hdt-mini-cart__checkout-pay-amount');
    if (checkoutPayEl && checkoutPayEl.textContent.trim()) {
      return checkoutPayEl.textContent.trim();
    }

    var totalValueEl = document.querySelector('.hdt-price-summary__row--total .hdt-mini-cart__value');
    if (totalValueEl && totalValueEl.textContent.trim()) {
      return totalValueEl.textContent.trim();
    }

    if (window.appliedDiscount && window.appliedDiscount.total != null) {
      var discountCents = discountTotalToCents(window.appliedDiscount.total);
      if (discountCents != null) return formatCartMoney(discountCents);
    }

    return formatCartMoney(roundCartToPayCents(computeTryonCartToPayCents(cart)));
  }

  function seedCartCacheFromDom(modal) {
    if (lastKnownCart || !modal) return;
    var bar = modal.querySelector('[data-tryon-cart-bar]');
    if (!bar) return;
    if (bar.hidden) {
      lastKnownCart = { item_count: 0, total_price: 0 };
      return;
    }
    var countEl = bar.querySelector('[data-tryon-cart-bar-count]');
    var match = countEl && countEl.textContent.match(/(\d+)/);
    var count = match ? parseInt(match[1], 10) : 1;
    lastKnownCart = { item_count: count, total_price: null };
  }

  function applyCartBarState(modal, cart, preferCartDataForPrice) {
    var bar = modal ? modal.querySelector('[data-tryon-cart-bar]') : null;
    if (!bar) return;

    var count = cart && cart.item_count != null ? cart.item_count : 0;
    if (count === 0) {
      bar.hidden = true;
      bar.setAttribute('aria-hidden', 'true');
      lastKnownCart = { item_count: 0, total_price: 0 };
      return;
    }

    bar.hidden = false;
    bar.removeAttribute('aria-hidden');

    var countEl = bar.querySelector('[data-tryon-cart-bar-count]');
    var priceEl = bar.querySelector('[data-tryon-cart-bar-price]');
    if (countEl) countEl.textContent = count === 1 ? '1 item' : count + ' items';
    if (priceEl && cart && cart.total_price != null) {
      priceEl.textContent = getCartToPayDisplayText(cart, !!preferCartDataForPrice);
    }

    lastKnownCart = {
      item_count: count,
      total_price: cart && cart.total_price != null
        ? cart.total_price
        : (lastKnownCart && lastKnownCart.total_price != null ? lastKnownCart.total_price : 0)
    };
  }

  function applyOptimisticCartAfterAdd(modal, addLine) {
    if (!addLine || typeof addLine !== 'object') return;
    var base = lastKnownCart || { item_count: 0, total_price: 0 };
    var addedQty = addLine.quantity || 1;
    var addedCents = addLine.final_line_price != null
      ? addLine.final_line_price
      : (addLine.line_price != null ? addLine.line_price : (addLine.price || 0) * addedQty);
    applyCartBarState(modal, {
      item_count: (base.item_count || 0) + addedQty,
      total_price: (base.total_price || 0) + addedCents
    }, true);
  }

  function fetchAndSyncCartBar(modal) {
    if (!modal) return Promise.resolve();
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart) {
          applyCartBarState(modal, { item_count: 0, total_price: 0 });
          return;
        }
        lastKnownCart = cart;
        applyCartBarState(modal, cart, true);
      })
      .catch(function () {
        if (!lastKnownCart || !lastKnownCart.item_count) {
          applyCartBarState(modal, { item_count: 0, total_price: 0 });
        }
      });
  }

  function onCartBarCartEvent(e) {
    var modal = document.querySelector('[data-tryon-modal]');
    if (!modal) return;
    var cart = e && e.detail && e.detail.cart;
    if (cart && typeof cart.item_count === 'number') {
      lastKnownCart = cart;
      applyCartBarState(modal, cart, true);
      return;
    }
    fetchAndSyncCartBar(modal);
  }

  function openSideCart() {
    // Re-use the theme's existing cart open helpers (same path the custom
    // sticky cart uses). Try Kwikcart first, then native Shopify drawer,
    // then header cart icon, then fall back to /cart.
    if (typeof window.fireViewCart === 'function') {
      try { window.fireViewCart('tryon_modal'); } catch (e) {}
    }
    if (typeof window.openGokwikSideCart === 'function') {
      try { window.openGokwikSideCart(); return; } catch (e) {}
    }
    var drawer = document.querySelector('hdt-cart-drawer');
    if (drawer && typeof drawer.openDialog === 'function') {
      drawer.openDialog();
      return;
    }
    if (drawer) {
      drawer.setAttribute('open', '');
      return;
    }
    var icon = document.querySelector('[aria-controls="CartDrawer"]');
    if (icon) { icon.click(); return; }
    window.location.href = '/cart';
  }

  function onClickViewCart() {
    // Close the modal first so the cart drawer is visible above it.
    close();
    setTimeout(openSideCart, 260);
  }

  // -------------------------------------------------------------------------
  // Open / close
  // -------------------------------------------------------------------------
  function lockScroll() {
    state.scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.classList.add('tryon-locked');
    document.body.style.top = '-' + state.scrollY + 'px';
  }

  function unlockScroll() {
    document.body.classList.remove('tryon-locked');
    document.body.style.top = '';
    window.scrollTo(0, state.scrollY || 0);
  }

  async function open(triggerEl) {
    var modal = document.querySelector('[data-tryon-modal]');
    if (!modal || state.open) return;

    state.open = true;
    state.color = (triggerEl && triggerEl.getAttribute('data-current-hex'))
               || (function () { try { return localStorage.getItem('lipstickColor'); } catch (e) { return null; } })()
               || '#a52a2a';

    if (triggerEl && triggerEl.getAttribute('data-current-hex')) {
      try { localStorage.setItem('lipstickColor', triggerEl.getAttribute('data-current-hex')); } catch (e) {}
    }

    seedCartCacheFromDom(modal);
    applyCartBarState(modal, lastKnownCart || { item_count: 0, total_price: 0 });

    lockScroll();
    modal.style.display = 'block';
    modal.removeAttribute('aria-hidden');
    requestAnimationFrame(function () { modal.classList.add('is-open'); });

    fetchAndSyncCartBar(modal);

    // Push a history entry so the system back button closes the modal first.
    try { history.pushState({ tryon: true }, '', location.href); } catch (e) {}

    await runTasks([
      function () { renderModelStrip(modal); },
      function () { renderPreview(modal); },
      function () { syncTryonCardFromVariant(modal); },
      function () {
        if (typeof window.reelVariantSaleTimerInit === 'function') {
          try { window.reelVariantSaleTimerInit(); } catch (e) {}
        }
      },
      function () {
        track('try_it_on', {
          product_handle: triggerEl ? triggerEl.getAttribute('data-product-handle') : null
        });
      },
      function () {
        track('use_try_on', {
          sku: triggerEl ? triggerEl.getAttribute('data-product-id') : null,
          productName: triggerEl ? triggerEl.getAttribute('data-product-title') : null
        });
      }
    ]);

    // Once the lip mask JSON resolves, paint the lips in whichever layer
    // is currently mounted (regular preview OR compare slider). Without
    // the compare-mode branch, opening Compare before the data loads
    // would leave the top SVG empty forever.
    loadLipData().then(function () { refreshLipsAnywhere(modal); });
  }

  function close(opts) {
    opts = opts || {};
    var modal = document.querySelector('[data-tryon-modal]');
    if (!modal || !state.open) return;
    state.open = false;

    if (state.compareSlider) {
      try { state.compareSlider.unmount(); } catch (e) {}
    }
    state.compareMode = false;
    var cmpBtn = modal.querySelector('[data-tryon-compare-toggle]');
    if (cmpBtn) {
      cmpBtn.setAttribute('aria-pressed', 'false');
      var label = cmpBtn.querySelector('.tryon-popup__compare-label');
      if (label) label.textContent = 'Compare';
    }

    modal.classList.remove('is-open');
    setTimeout(function () {
      modal.style.display = 'none';
      modal.setAttribute('aria-hidden', 'true');
      unlockScroll();
    }, 250);

    if (!opts.skipHistory && history.state && history.state.tryon) {
      try { history.back(); } catch (e) {}
    }

    track('back_arrow_tryon', {});
  }

  // -------------------------------------------------------------------------
  // Global event delegation
  // -------------------------------------------------------------------------
  document.addEventListener('click', function (e) {
    var modal = document.querySelector('[data-tryon-modal]');

    // Open button anywhere on the page
    var openBtn = e.target.closest && e.target.closest('[data-tryon-open]');
    if (openBtn) { e.preventDefault(); open(openBtn); return; }

    if (!modal) return;

    // The rest of the delegated handlers only fire inside the modal
    if (!modal.contains(e.target)) return;

    if (e.target.closest('[data-tryon-close]')) { close(); return; }

    var modelBtn = e.target.closest('[data-tryon-model]');
    if (modelBtn) {
      onClickModelThumb(modal, modelBtn.getAttribute('data-tryon-model'));
      return;
    }

    var compareToggle = e.target.closest('[data-tryon-compare-toggle]');
    if (compareToggle) { onClickCompareToggle(modal, compareToggle); return; }

    if (e.target.closest('[data-tryon-coachmark-ok]')) {
      dismissCompareCoachmark(modal);
      return;
    }

    var swatchBtn = e.target.closest('.tryon-v-btn');
    if (swatchBtn) {
      e.preventDefault();
      e.stopPropagation();
      onClickShadeSwatch(modal, swatchBtn);
      return;
    }

    var atcBtn = e.target.closest('[data-tryon-atc]');
    if (atcBtn) { onClickAtc(modal, atcBtn); return; }

    if (e.target.closest('[data-tryon-view-cart]')) {
      onClickViewCart();
      return;
    }
  }, true);

  // System back button → close the modal
  window.addEventListener('popstate', function () {
    if (state.open) close({ skipHistory: true });
  });

  // Escape key (desktop convenience)
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && state.open) close();
  });

  document.addEventListener('cart:refresh', onCartBarCartEvent);
  document.addEventListener('cart:change', onCartBarCartEvent);

  seedCartCacheFromDom(document.querySelector('[data-tryon-modal]'));
})();
